// One entry point on the Entry points tab (spec 11 6.15.10, mock n05): the header with its kind, add-on chips and edge
// host; Domains and Routes from the status endpoint; the pending origins, certificate access and rotation callouts.
import { useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronRight,
  CircleCheck,
  CircleDashed,
  CircleX,
  Clock,
  Loader,
  Lock,
  MoreHorizontal,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react';
import { Fragment, useState } from 'react';
import { Link } from 'react-router';
import { useApi } from '../../api/context';
import { queryKeys, useEntryPointStatus } from '../../api/queries';
import type {
  EdgeKind,
  EntryPointDomainStatus,
  EntryPointRouteStatus,
  EntryPointSettings,
  EntryPointStatus,
  Network,
} from '../../api/types';
import { useToast } from '../../components/toast';
import { CopyButton, ErrorMessage, Loading } from '../../components/ui';
import { formatRelative } from '../../lib/format';
import { MenuButton, type MenuEntry } from '../../shell/Menu';
import { strings } from '../../strings';
import { addOnChips, hostPaths, inactiveWords, kindLine, vaultOf } from './helpers';

const e = strings.networks.entryPoints;

function DomainState({
  domain,
  lastKnownAt,
}: {
  domain: EntryPointDomainStatus;
  lastKnownAt: string | null;
}) {
  const cert = domain.certificate === 'managed' ? e.managedCertificate : e.keyVaultCertificate;
  switch (domain.state) {
    case 'validated':
      return (
        <span className="badge tone-success">
          <CircleCheck aria-hidden className="size-3.5" />
          {e.domainState.validated} · {cert}
        </span>
      );
    case 'waiting-for-dns':
      return (
        <span className="badge tone-warning">
          <Clock aria-hidden className="size-3.5" />
          {e.domainState['waiting-for-dns']}
        </span>
      );
    case 'failed':
      return (
        <span className="badge tone-danger">
          <CircleX aria-hidden className="size-3.5" />
          {e.domainState.failed}
        </span>
      );
    case 'unknown':
      return (
        <span className="badge tone-neutral">
          <CircleDashed aria-hidden className="size-3.5" />
          {e.domainState.unknown}
          {lastKnownAt ? ` · ${e.lastKnown(formatRelative(lastKnownAt))}` : ''}
        </span>
      );
    default:
      return (
        <span className="badge tone-neutral">
          <CircleDashed aria-hidden className="size-3.5" />
          {e.domainState.pending}
        </span>
      );
  }
}

function DomainNext({ domain }: { domain: EntryPointDomainStatus }) {
  if (domain.state === 'validated' && domain.certificate === 'managed')
    return <span className="text-fg-muted">{e.renews}</span>;
  if (domain.state !== 'waiting-for-dns' && domain.state !== 'failed') return null;
  return (
    <ul className="space-y-1">
      {domain.records.map((r) => (
        <li key={`${r.type}-${r.name}`} className="flex flex-wrap items-center gap-1">
          <span className="min-w-0 break-all">
            {e.createVerb} {r.type} <code className="font-mono text-[12px]">{r.name}</code> ={' '}
            <code className="font-mono text-[12px]">{r.value}</code>
          </span>
          <CopyButton value={r.value} label={e.copyRecord(r.type, r.name)} />
        </li>
      ))}
    </ul>
  );
}

function Health({ route, network }: { route: EntryPointRouteStatus; network: Pick<Network, 'name'> }) {
  if (route.originState === 'waiting-for-apply')
    return (
      <span className="badge tone-neutral">
        <Loader aria-hidden className="size-3.5" />
        {e.waitingForApply}
      </span>
    );
  if (route.originState === 'inactive')
    return (
      <span className="badge tone-warning whitespace-normal">
        <TriangleAlert aria-hidden className="size-3.5 shrink-0" />
        {e.inactive(inactiveWords(route.inactiveReason, route.origin?.deploymentName ?? '', network.name))}
      </span>
    );
  if (route.originState === 'not-applied')
    return (
      <span className="badge tone-neutral">
        <CircleDashed aria-hidden className="size-3.5" />
        {e.notAppliedRoute}
      </span>
    );
  switch (route.health) {
    case 'healthy':
      return (
        <span className="badge tone-success">
          <CircleCheck aria-hidden className="size-3.5" />
          {e.healthWords.healthy}
        </span>
      );
    case 'unhealthy':
      return (
        <span className="badge tone-danger">
          <CircleX aria-hidden className="size-3.5" />
          {e.healthWords.unhealthy}
        </span>
      );
    case 'not-probed':
      return (
        <span className="badge tone-neutral" title={e.notProbedHint}>
          <CircleDashed aria-hidden className="size-3.5" />
          {e.healthWords['not-probed']}
          <span className="sr-only">: {e.notProbedHint}</span>
        </span>
      );
    default:
      return (
        <span className="badge tone-neutral">
          <CircleDashed aria-hidden className="size-3.5" />
          {e.healthWords.unknown}
        </span>
      );
  }
}

function Access({ route }: { route: EntryPointRouteStatus }) {
  const locked = route.strength === 'strongest' || route.strength === 'strong';
  return locked ? (
    <span className="inline-flex items-start gap-1">
      <Lock aria-hidden className="mt-0.5 size-3.5 shrink-0" />
      {route.accessText}
    </span>
  ) : (
    <span className="inline-flex items-start gap-1 text-[var(--warning)]">
      <TriangleAlert aria-hidden className="mt-0.5 size-3.5 shrink-0" />
      {route.accessText}
    </span>
  );
}

function Origin({ route }: { route: EntryPointRouteStatus }) {
  if (!route.origin) return <span className="text-fg-muted">–</span>;
  const o = route.origin;
  const cloud = o.provider && o.provider !== 'azure' ? e.crossCloud[o.provider] : null;
  return (
    <span>
      <code className="font-mono text-[12px] font-semibold">
        {o.deploymentName ?? o.deploymentId} / {o.resourceId}
      </code>{' '}
      <span className="text-[12px] text-fg-muted">
        {[o.platformLabel, cloud].filter(Boolean).join(' · ')}
      </span>
    </span>
  );
}

function rotationWords(step: EntryPointStatus['rotations'][number], network: string) {
  switch (step.step) {
    case 'requested':
      return e.rotationStep.requested(network);
    case 'switching':
      return e.rotationStep.switching(network);
    case 'dropping':
      return e.rotationStep.dropping(network);
    default:
      return null;
  }
}

/** The callouts under the tables: pending origins, certificate access, rotations, a failed read. */
function Callouts({ status, network }: { status: EntryPointStatus; network: Pick<Network, 'name'> }) {
  const deploymentLink = (d: { deploymentId: string; deploymentName: string }) => (
    <Link key={d.deploymentId} className="link underline" to={`/deployments/${d.deploymentId}`}>
      {d.deploymentName}
    </Link>
  );
  const join = (items: { deploymentId: string; deploymentName: string }[]) =>
    items.map((d, i) => (
      <Fragment key={d.deploymentId}>
        {i > 0 && ', '}
        {deploymentLink(d)}
      </Fragment>
    ));
  const [before, after] = e.pendingOrigins('\u0000').split('\u0000');
  return (
    <>
      {status.lastKnownAt && (
        <p className="callout callout-warning" data-testid="ep-stale">
          <TriangleAlert aria-hidden className="size-4" />
          <span>
            {e.stale(formatRelative(status.lastKnownAt))} {status.messages.join(' ')}
          </span>
        </p>
      )}
      {status.pendingOrigins.length > 0 && (
        <p className="callout callout-warning" data-testid="ep-pending-origins">
          <TriangleAlert aria-hidden className="size-4" />
          <span>
            {before}
            {join(status.pendingOrigins)}
            {after}
          </span>
        </p>
      )}
      {status.certificateAccess?.state === 'pending' && (
        <div className="callout callout-warning" data-testid="ep-certificate-access">
          <TriangleAlert aria-hidden className="size-4" />
          <span className="flex flex-wrap items-center gap-1">
            {e.certificateAccess(
              vaultOf(status.certificateAccess.secretIds[0]),
              status.certificateAccess.identityPrincipalId ?? '',
              network.name,
            )}
            {status.certificateAccess.identityPrincipalId && (
              <CopyButton value={status.certificateAccess.identityPrincipalId} label={e.copyPrincipal} />
            )}
          </span>
        </div>
      )}
      {status.rotations.map((r) => (
        <p key={r.routeId} className="callout callout-info" data-testid={`ep-rotation-${r.routeId}`}>
          <Clock aria-hidden className="size-4" />
          <span>
            {e.rotation(r.routeId)}{' '}
            {r.step === 'delivering' ? (
              <>
                {e.rotationStep.delivering} {join(r.blockedBy)} {e.rotationStep.deliveringAfter}
              </>
            ) : (
              rotationWords(r, network.name)
            )}
          </span>
        </p>
      ))}
    </>
  );
}

function Tables({ status, network }: { status: EntryPointStatus; network: Pick<Network, 'name'> }) {
  return (
    <>
      <h3 className="net-ep-heading">{e.domains}</h3>
      {status.domains.length === 0 ? (
        <p className="help">{status.hostName ? status.hostName : e.appliesFirst}</p>
      ) : (
        <table className="table table-cards net-ep-table">
          <caption className="sr-only">{e.domainsCaption(status.entryPointId)}</caption>
          <thead>
            <tr>
              <th scope="col">{e.hostName}</th>
              <th scope="col">{e.state}</th>
              <th scope="col">{e.next}</th>
            </tr>
          </thead>
          <tbody>
            {status.domains.map((d) => (
              <tr key={d.hostname} data-testid={`ep-domain-${d.hostname}`}>
                <td data-label={e.hostName}>
                  <code className="font-mono text-[12.5px]">{d.hostname}</code>
                </td>
                <td data-label={e.state}>
                  <DomainState domain={d} lastKnownAt={status.lastKnownAt} />
                </td>
                <td data-label={e.next}>
                  <DomainNext domain={d} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <h3 className="net-ep-heading">{e.routes}</h3>
      {status.routes.length === 0 ? (
        <p className="help">{e.noRoutes}</p>
      ) : (
        <table className="table table-cards net-ep-table">
          <caption className="sr-only">{e.routesCaption(status.entryPointId)}</caption>
          <thead>
            <tr>
              <th scope="col">{e.hostAndPath}</th>
              <th scope="col">{e.origin}</th>
              <th scope="col">{e.originAccess}</th>
              <th scope="col">{e.health}</th>
            </tr>
          </thead>
          <tbody>
            {status.routes.flatMap((r) =>
              hostPaths(r, status.hostName).map((hp) => (
                <tr key={`${r.routeId}-${hp}`} data-testid={`ep-route-${r.routeId}`}>
                  <td data-label={e.hostAndPath}>
                    <code className="font-mono text-[12.5px] break-all">{hp}</code>
                  </td>
                  <td data-label={e.origin}>
                    <Origin route={r} />
                  </td>
                  <td data-label={e.originAccess}>
                    <Access route={r} />
                  </td>
                  <td data-label={e.health}>
                    <Health route={r} network={network} />
                  </td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      )}
      <Callouts status={status} network={network} />
    </>
  );
}

export function EntryPointCard({
  network,
  ep,
  kind,
  expanded,
  onToggle,
  manage,
  onEdit,
  onRotate,
  onRemove,
}: {
  network: Network;
  ep: EntryPointSettings;
  kind: EdgeKind | undefined;
  expanded: boolean;
  onToggle: () => void;
  manage: boolean;
  onEdit: () => void;
  onRotate: () => void;
  onRemove: () => void;
}) {
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const status = useEntryPointStatus(network.id, ep.id);
  const [refreshing, setRefreshing] = useState(false);
  const s = status.data;
  const chips = addOnChips(ep, kind);
  const edge = s?.hostName ?? s?.publicIp ?? null;
  const secretRoutes = (s?.routes ?? []).some(
    (r) => r.access === 'header' || r.access === 'allow-list-and-header',
  );
  const refresh = async () => {
    setRefreshing(true);
    try {
      const fresh = await api.getEntryPointStatus(network.id, ep.id, true);
      queryClient.setQueryData(queryKeys.entryPointStatus(network.id, ep.id), fresh);
    } catch (error) {
      toast.error(error);
    } finally {
      setRefreshing(false);
    }
  };
  const entries: MenuEntry[] = [
    { kind: 'action', id: 'edit', label: e.edit, onSelect: onEdit },
    ...(secretRoutes ? [{ kind: 'action' as const, id: 'rotate', label: e.rotate, onSelect: onRotate }] : []),
    { kind: 'action', id: 'refresh', label: e.refresh, onSelect: () => void refresh() },
    { kind: 'separator', id: 'sep' },
    { kind: 'action', id: 'remove', label: e.remove, onSelect: onRemove },
  ];
  const first = s?.routes[0];
  const bodyId = `ep-body-${ep.id}`;
  return (
    <article
      className={`net-ep-card ${expanded ? 'is-expanded' : ''}`}
      aria-labelledby={`ep-title-${ep.id}`}
      data-testid={`entry-point-${ep.id}`}
    >
      <header className="net-ep-head">
        <button
          type="button"
          className="btn btn-ghost btn-icon"
          aria-expanded={expanded}
          aria-controls={expanded ? bodyId : undefined}
          aria-label={expanded ? e.collapse(ep.id) : e.expand(ep.id)}
          onClick={onToggle}
        >
          {expanded ? (
            <ChevronDown aria-hidden className="size-4" />
          ) : (
            <ChevronRight aria-hidden className="size-4" />
          )}
        </button>
        <span aria-hidden className="net-edge-abbr">
          {kind?.abbr ?? ep.kind.slice(0, 3).toUpperCase()}
        </span>
        <h3 id={`ep-title-${ep.id}`} className="font-semibold">
          {ep.id}
        </h3>
        <span className="text-fg-muted">{kindLine(ep, kind)}</span>
        <span className="flex flex-wrap gap-1">
          {chips.map((chip) => (
            <span
              key={chip.id}
              className={`badge tone-${chip.tone}`}
              title={chip.hint}
              data-testid={`ep-chip-${chip.id}`}
            >
              {chip.tone === 'success' ? (
                <ShieldCheck aria-hidden className="size-3.5" />
              ) : chip.tone === 'warning' ? (
                <TriangleAlert aria-hidden className="size-3.5" />
              ) : null}
              {chip.text}
              {chip.hint && <span className="sr-only">: {chip.hint}</span>}
            </span>
          ))}
          {s && !s.applied && <span className="badge tone-neutral">{e.notApplied}</span>}
          {s?.changedSinceApply && <span className="badge tone-warning">{e.changed}</span>}
        </span>
        {!expanded && first && (
          <span className="net-ep-oneline">
            <code className="font-mono text-[12px]">
              {hostPaths(first, s?.hostName ?? null)[0]} → {first.origin?.deploymentName ?? ''}
              {first.origin ? ` / ${first.origin.resourceId}` : ''}
            </code>
            <span className="text-[12px] text-fg-muted">{e.accessLabel[first.access] ?? first.access}</span>
            <Health route={first} network={network} />
          </span>
        )}
        <span className="ml-auto flex items-center gap-1">
          {expanded && edge && (
            <>
              <code className="font-mono text-[12.5px] text-fg-muted">{edge}</code>
              <CopyButton value={edge} label={s?.hostName ? e.copyHost : e.copyIp} />
            </>
          )}
          {refreshing && <Loader aria-hidden className="size-4 animate-spin" />}
          {manage && (
            <MenuButton
              buttonContent={<MoreHorizontal aria-hidden className="size-4" />}
              buttonLabel={e.actions(ep.id)}
              buttonClassName="btn btn-icon"
              menuLabel={e.actions(ep.id)}
              entries={entries}
              placement="below-end"
              portal
            />
          )}
        </span>
      </header>
      {expanded && (
        <div id={bodyId} className="net-ep-body">
          {status.error ? (
            <ErrorMessage error={status.error} onRetry={() => void status.refetch()} />
          ) : !s ? (
            <Loading />
          ) : (
            <Tables status={s} network={network} />
          )}
        </div>
      )}
    </article>
  );
}
